// Wraps any chip (RolloutBadge, StateChip, etc.) with the shared
// "hover shows workspaces / click opens modal" behavior. Extracted from
// what used to live inside RolloutBadgeWithWorkspaces so the same
// affordance can hang off any EA/GA-ish pill in the app.
//
// Data model:
//   flags: the FeatureFlag[] context to derive workspaces from
//   title: header for the click-open modal (feature name, item text, …)
//
// If we have zero flags or the workspaces list hasn't loaded yet, the
// wrap becomes a no-op — just renders the children with no interactivity
// (so it stays cheap to sprinkle everywhere).
import { useMemo, useState } from "react";
import type { FeatureFlag } from "@release-hub/shared";
import { useWorkspaces } from "@/api/queries";
import { effectiveWorkspaces } from "@/lib/featureWorkspaces";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";

const HOVER_PREVIEW = 8;

export function WorkspaceHoverWrap({
  flags,
  title,
  children,
}: {
  flags: FeatureFlag[];
  title: string;
  children: React.ReactNode;
}) {
  const { data: workspaces } = useWorkspaces();
  const [hovered, setHovered] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { names, isEveryone } = useMemo(() => {
    if (!workspaces) return { names: [] as string[], isEveryone: false };
    const eff = effectiveWorkspaces(flags, workspaces);
    const wsById = new Map(workspaces.map((w) => [w.id, w.name]));
    const namesSorted = eff.ids
      .map((id) => wsById.get(id) ?? id)
      .sort((a, b) => a.localeCompare(b));
    return { names: namesSorted, isEveryone: eff.isEveryone };
  }, [flags, workspaces]);

  // Nothing to show — render children as-is with no interactivity.
  if (!workspaces || flags.length === 0 || (names.length === 0 && !isEveryone)) {
    return <>{children}</>;
  }

  const openModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setModalOpen(true);
  };

  return (
    <>
      <span
        className="relative inline-flex cursor-pointer align-middle"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={openModal}
      >
        {children}
        {hovered && (
          <span className="absolute right-0 top-full z-30 mt-1 w-64 rounded-md border border-border bg-card p-3 text-left shadow-lg">
            <span className="block text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
              {isEveryone
                ? "All workspaces"
                : `${names.length} workspace${names.length === 1 ? "" : "s"} with access`}
            </span>
            {!isEveryone && (
              <span className="mt-1.5 block space-y-0.5 text-xs text-text">
                {names.slice(0, HOVER_PREVIEW).map((n) => (
                  <span key={n} className="block truncate">
                    {n}
                  </span>
                ))}
                {names.length > HOVER_PREVIEW && (
                  <span className="block text-text-tertiary">
                    +{names.length - HOVER_PREVIEW} more — click for full list
                  </span>
                )}
              </span>
            )}
          </span>
        )}
      </span>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-h-[80vh] max-w-md overflow-hidden">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {isEveryone
                ? "Available to all squapi workspaces."
                : `Available in ${names.length} workspace${names.length === 1 ? "" : "s"}. Deleted workspaces filtered out.`}
            </DialogDescription>
          </DialogHeader>
          {!isEveryone && (
            <ul className="max-h-[60vh] space-y-0.5 overflow-y-auto text-sm">
              {names.map((n) => (
                <li key={n} className="truncate rounded px-2 py-1 hover:bg-border/40">
                  {n}
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
